import styled, { css, keyframes } from 'styled-components'
import { tokens } from 'styling/tokens'
import { BodyText } from 'styling/Typography'
import { IStatusPill } from 'types/ui/statusPill.interface'

const toneStyle = ($tone: IStatusPill['status']) => css`
  background: ${tokens.status[$tone].bg};
  border: 1px solid ${tokens.status[$tone].fg};
  color: ${tokens.status[$tone].fg};
`

const hourGridlines = ($divisions: number) => css`
  background-image: linear-gradient(to right, ${tokens.border.hairline} 1px, transparent 1px);
  background-size: calc(100% / ${$divisions}) 100%;
`

export const StripWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  width: 100%;
`

export const StripHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
`

export const StripRuler = styled.div<{ $divisions: number }>`
  position: relative;
  height: 1.25rem;
  border-bottom: 1px solid ${tokens.border.hairline};
  ${({ $divisions }) => hourGridlines($divisions)};
`

export const StripTick = styled.span<{ $pct: number; $last: boolean }>`
  position: absolute;
  bottom: 0.125rem;
  left: ${({ $pct }) => $pct}%;
  transform: ${({ $pct, $last }) =>
    $pct === 0 ? 'none' : $last ? 'translateX(-100%)' : 'translateX(-50%)'};
  font-size: ${tokens.font.micro};
  font-weight: 600;
  letter-spacing: 0.04em;
  color: ${tokens.text.muted};
  white-space: nowrap;
`

export const StripTrack = styled.div<{ $divisions: number }>`
  position: relative;
  height: 2.5rem;
  border-radius: ${tokens.radius.control};
  background: ${tokens.surface.inset};
  ${({ $divisions }) => hourGridlines($divisions)};
`

export const ShiftBoundaryLine = styled.div<{ $pct: number }>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: ${({ $pct }) => $pct}%;
  width: 1px;
  background: ${tokens.accent.base};
  opacity: 0.7;
  z-index: 2;
  pointer-events: none;
`

export const BoundaryLabelRow = styled.div`
  position: relative;
  height: 1rem;
`

export const BoundaryLabel = styled.span<{ $pct: number }>`
  position: absolute;
  top: 0;
  left: ${({ $pct }) => $pct}%;
  transform: translateX(-50%);
  font-size: ${tokens.font.micro};
  color: ${tokens.text.muted};
  white-space: nowrap;
`

export const StripSeg = styled.div<{
  $tone: IStatusPill['status']
  $left: number
  $width: number
  $clickable: boolean
}>`
  position: absolute;
  top: 50%;
  margin-top: -0.875rem;
  height: 1.75rem;
  left: ${({ $left }) => $left}%;
  width: ${({ $width }) => $width}%;
  min-width: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: 4px;
  font-size: ${tokens.font.micro};
  font-weight: 600;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  transition: transform ${tokens.motion.duration} ${tokens.motion.ease},
    box-shadow ${tokens.motion.duration} ${tokens.motion.ease};
  ${({ $tone }) => toneStyle($tone)};

  &:hover {
    transform: scaleY(1.12);
    box-shadow: ${tokens.shadow.card};
    z-index: 1;
  }
`

export const SegGlyph = styled.span`
  opacity: 0.9;
`

export const SegLabel = styled.span`
  padding: 0 0.25rem;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`

export const LegendRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.75rem 1.5rem;
  margin-top: 0.5rem;
`

export const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: help;
`

export const LegendSwatch = styled.span<{ $tone: IStatusPill['status'] }>`
  width: 1rem;
  height: 0.75rem;
  border-radius: 4px;
  flex-shrink: 0;
  ${({ $tone }) => toneStyle($tone)};
`

export const BarTrack = styled.div`
  position: relative;
  width: 5rem;
  height: 0.375rem;
  border-radius: 999px;
  background: ${tokens.surface.inset};
  border: 1px solid ${tokens.border.hairline};
  overflow: hidden;
  flex-shrink: 0;
`

export const BarFill = styled.div<{ $pct: number; $tone: IStatusPill['status'] }>`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  width: ${({ $pct }) => $pct}%;
  border-radius: 999px;
  background: ${({ $tone }) => tokens.status[$tone].fg};
`

export const DangerDot = styled.span`
  display: inline-block;
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background: ${tokens.status.danger.fg};
  flex-shrink: 0;
`

export const ToneValue = styled(BodyText)<{ $tone?: IStatusPill['status'] }>`
  font-weight: 600;
  ${({ $tone }) => $tone && `color: ${tokens.status[$tone].fg};`}
`

export const TotalsWrap = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1.5rem;
  flex-wrap: wrap;
  width: 100%;
`

export const BucketRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 18rem;
`

export const BucketRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.375rem 0.5rem;
  border-radius: ${tokens.radius.control};
  background: ${tokens.surface.inset};
`

export const StatGrid = styled.div`
  flex: 1;
  min-width: 20rem;
  display: grid;
  grid-template-columns: repeat(4, minmax(7rem, 1fr));
  gap: 0.75rem;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, 1fr);
  }
`

export const StatCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.75rem;
  border-radius: ${tokens.radius.control};
  background: ${tokens.surface.inset};
  border: 1px solid ${tokens.border.hairline};
`

export const DayBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid ${tokens.border.hairline};

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
`

const nowPulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
`

export const NowMarkerLine = styled.div<{ $pct: number }>`
  position: absolute;
  top: -0.25rem;
  bottom: -0.25rem;
  left: ${({ $pct }) => $pct}%;
  width: 2px;
  border-radius: 999px;
  background: ${tokens.status.danger.fg};
  z-index: 3;
  pointer-events: none;
  animation: ${nowPulse} 1.6s ${tokens.motion.ease} infinite;
`

export const NowBoundaryLabel = styled.span<{ $pct: number }>`
  position: absolute;
  top: 0;
  left: ${({ $pct }) => $pct}%;
  transform: translateX(-50%);
  font-size: ${tokens.font.micro};
  font-weight: 600;
  color: ${tokens.status.danger.fg};
  white-space: nowrap;
  animation: ${nowPulse} 1.6s ${tokens.motion.ease} infinite;
`

export const LiveSection = styled.div`
  width: 100%;
  border: 1px dashed ${tokens.border.hairline};
  border-radius: ${tokens.radius.card};
  background: ${tokens.surface.glass};
  backdrop-filter: blur(${tokens.blur});
  box-shadow: ${tokens.shadow.card};
  overflow: hidden;
`

export const UnlockedTag = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.5rem;
  border-radius: ${tokens.radius.chip};
  border: 1px dashed ${tokens.status.warning.fg};
  background: ${tokens.status.warning.bg};
  color: ${tokens.status.warning.fg};
  font-size: ${tokens.font.micro};
  font-weight: 600;
  letter-spacing: 0.04em;
  white-space: nowrap;
  opacity: 0.85;
`
